import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ContentSection, Wizard } from '@vwfs-bronson/bronson-react';
import { TRoutesValues } from '../../routing/routes';
import { useStepFromUrl } from '../../services/common/miscUtils';
import { RootState } from '../../services/redux';
import { customerSelectors } from '../../services/redux/features/customer.slice';
import { quoteSelectors } from '../../services/redux/features/quote.slice';
import { useNavigate } from '../../services/routing/navigate';

const wizardSteps = [
  {
    step: 1,
    name: 'Bien',
  },
  {
    step: 2,
    name: 'Prospect',
  },
  {
    step: 3,
    name: 'Cotation',
  },
  {
    step: 4,
    name: 'Complétude',
  },
] as const;

const isStepDone = (
  step: number,
  current: number | null,
  isHybridJourney: boolean,
  isQuoteEditable: boolean,
  maxStepReached: number
) => {
  switch (true) {
    case !isQuoteEditable:
      return true;
    case !current:
    case isHybridJourney:
      return false;
    default:
      return step < current! || step <= maxStepReached;
  }
};

const isStepDisabled = (step: number, current: number | null, isHybridJourney: boolean, maxStepReached: number) => {
  switch (true) {
    case !!current && current > 4:
    case !current:
    case step < 4 && isHybridJourney:
      return true;
    case step <= maxStepReached:
      return false;
    default:
      return step > (current ?? 1);
  }
};

const routesWithDisabledWizard = ['/form/5'] satisfies Array<TRoutesValues | `/form/${number}`>;

export const AppWizard: FunctionComponent = () => {
  const dispatch = useDispatch();
  const isHybridJourney = useSelector((state: RootState) => state.custom.isHybridJourney);
  const [maxStepReached, setMaxStepReached] = useState(1);
  const currentStep = useStepFromUrl();
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);
  const hasCoCustomer = useSelector(customerSelectors.selectHasCoCustomer);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (currentStep && currentStep > maxStepReached) {
      setMaxStepReached(currentStep);
    }
  }, [currentStep, maxStepReached, dispatch]);

  const navigateToStep = (step: number) => {
    if (step !== currentStep) {
      navigate.form(`/${step}`, hasCoCustomer && step === 2 ? '/coContractor' : undefined);
    }
  };

  const isRouteWithDisabledWizard = routesWithDisabledWizard.some((r) => location.pathname.startsWith(r));

  return (
    <ContentSection pageWrap className="u-pb-none">
      <Wizard
        stepLabels={{
          doneLabel: 'completed',
          errorLabel: 'invalid',
          notDoneLabel: 'not completed',
          noInteractionLabel: 'no interaction',
        }}
      >
        {wizardSteps.map(({ step, name }) => (
          <Wizard.Item
            active={step === currentStep}
            noInteraction={
              isStepDisabled(step, currentStep, isHybridJourney, maxStepReached) || isRouteWithDisabledWizard
            }
            done={isStepDone(step, currentStep, isHybridJourney, isQuoteEditable, maxStepReached)}
            key={step}
            onClick={() => navigateToStep(step)}
          >
            {name}
          </Wizard.Item>
        ))}
      </Wizard>
    </ContentSection>
  );
};
